import { useLayoutWidth } from 'app/imports/App.hooks'
import { ReactComponent as IconCamera } from 'assets/img/FaCamera.svg'
import IconSchool from 'assets/img/IconSchool.png'
import { ReactComponent as IconNfhsLogoLive } from 'assets/img/nfhs-logo-live.svg'
import { cszUrl } from 'features/Env'
import { useCallback } from 'react'
import { Game, Schedule } from 'types'
import { formatEventTime, formatPrintEventDate } from 'utils'
import * as Styled from './ScheduleTable.styled'

export function ScheduleTable({ schedule }: { schedule: Schedule }) {
  const layoutWidth = useLayoutWidth()
  const hasScore = (value: string) => value !== null && value !== undefined

  const getAt = useCallback((game: Game) => {
    if (game.noHomeTeam) {
      return ''
    }

    return game.isHome ? 'vs' : '@'
  }, [])

  const getOpponentCell = useCallback(
    (game: Game) => {
      if (game.isMultiteam) {
        return (
          <Styled.OpponentCellMultiTeam>
            {game.name || 'Multi Team Event'}
          </Styled.OpponentCellMultiTeam>
        )
      }

      return (
        <Styled.OpponentCell>
          <Styled.OpponentCellAt as="b">{getAt(game)}</Styled.OpponentCellAt>
          <Styled.TeamAvatar src={game.opponent.imgUrl || IconSchool} alt="school" />
          <Styled.OpponentName title={game.opponent.shortName || game.opponent.name}>
            {game.opponent.shortName || game.opponent.name}
          </Styled.OpponentName>
        </Styled.OpponentCell>
      )
    },
    [getAt],
  )

  const handleGameClick = (gameId: string) => () => {
    window.open(`${cszUrl}/scores/view/${gameId}`, '_blank')
  }

  return (
    <>
      {schedule.gamesPast.length > 0 && (
        <Styled.Table>
          <Styled.TableHeader $width={layoutWidth}>
            <div>DATE</div>
            <div>OPPONENT</div>
            <div>RESULT</div>
            <div>W-L-T</div>
          </Styled.TableHeader>

          {schedule.gamesPast.map((game) => {
            return (
              <Styled.TableRow
                key={game.id}
                onClick={handleGameClick(game.id)}
                $width={layoutWidth}
              >
                <Styled.TableCell>
                  {formatPrintEventDate(game.eventDate, game.isEventDateTBD, 'EEE, MMM d')}
                </Styled.TableCell>

                {getOpponentCell(game)}

                <Styled.TableCell>
                  {!game.canSubmitScores && game.gameType !== 'Scrimmage' && (
                    <>
                      <Styled.GameScoreStatus
                        $isWin={game.gameScoreStatus === 'W'}
                        $isLose={game.gameScoreStatus === 'L'}
                      >
                        {game.gameScoreStatus}
                      </Styled.GameScoreStatus>
                      {hasScore(game.myScore) && hasScore(game.opponentScore) && (
                        <div>{`${game.myScore}-${game.opponentScore}`}</div>
                      )}
                    </>
                  )}
                </Styled.TableCell>

                <Styled.TableCell>
                  {game.wlt && (
                    <div>{`${game.wlt.win} - ${game.wlt.loss}${game.wlt.tie ? ` - ${game.wlt.tie}` : ''}`}</div>
                  )}
                  {(game.youtubeLink || game.nfhsLink) && <Styled.CameraIcon as={IconCamera} />}
                </Styled.TableCell>
              </Styled.TableRow>
            )
          })}
        </Styled.Table>
      )}

      {schedule.gamesFuture.length > 0 && (
        <Styled.Table>
          <Styled.TableHeader $width={layoutWidth}>
            <div>DATE</div>
            <div>OPPONENT</div>
            <div>TIME</div>
            <div>STREAM</div>
          </Styled.TableHeader>

          {schedule.gamesFuture.map((game) => {
            const gameTime = formatEventTime(game.startTime)

            return (
              <Styled.TableRow
                key={game.id}
                onClick={handleGameClick(game.id)}
                $width={layoutWidth}
              >
                <Styled.TableCell>
                  {formatPrintEventDate(game.eventDate, game.isTBD, 'EEE, MMM d')}
                </Styled.TableCell>

                {getOpponentCell(game)}

                <Styled.TableCell>{gameTime}</Styled.TableCell>

                <Styled.TableCell>
                  {game.nfhsLink && (
                    <Styled.NFHSButton $width={layoutWidth}>
                      <IconNfhsLogoLive />
                    </Styled.NFHSButton>
                  )}
                </Styled.TableCell>
              </Styled.TableRow>
            )
          })}
        </Styled.Table>
      )}
    </>
  )
}
